/******************************************
 *  Author : vsuthar
 *  Created On : Wed Sep 01 2021
 *  File : TreeReduer.js
 *  ©Palo Alto Networks
 *******************************************/
import { groupBy, keyBy } from 'lodash'
import { setSelectedTSG, getCommonModuleState } from '../utils/Sparky'
import FlattenTree from '../utils/FlattenTree'
import { ROOT_NODE_ID } from '../../tenancy/constants/TenancyConstants'
import {
  LOAD_TREE_DATA,
  LOAD_INSTANCE_DATA,
  LOAD_TENANT_INSTANCE_DATA,
  LOAD_CHILD_INSTANCE_DATA,
  LOAD_TSG_ID,
  RESET_TREE_DATA,
  ON_EXPANDED_KEY,
  SELECTED_TREE_NODE
} from './actions/TreeReducer.actions'

const initialState = {
  globalTsgId: '',
  globalTsgName: '',
  selectedTreeNode: undefined,
  sgTree: [],
  sgKeyBy: {},
  flattenTree: [],
  breadcrumbs: new Map(),
  instances: [],
  instancesAndTreeNode: [],
  instancesKeyBy: {},
  expandedKeys: [],
  childTenantInstances: [],
  parentTenantInstances: [],
  selectedTreeRoot: {},
  tsgDropDownDetails: []
}
const getInitialState = () => ({
  ...initialState,
  selectedTreeNode: getCommonModuleState()?.tsg,
})

const createBreadCrumbs = (obj, breadcrumbs, sgKeyBy) => {
  if (obj?.id) {
    breadcrumbs.set(obj.id, obj)
    createBreadCrumbs(sgKeyBy[obj.parent_id], breadcrumbs, sgKeyBy)
  }
}

/**
 *
 * @param instancesKeyBy
 * @param instances
 * @param treeNode
 * @returns {*[]}
 */
const getInstancesBasedOnTreeNode = ({ sgKeyBy }, instances) => {
  const instancesAndTree = instances.map(each => {
    const tsgId = each?.tsg_id
    if (tsgId) {
      return {
        ...each,
        display_name: sgKeyBy[tsgId]?.display_name,
        vertical: sgKeyBy[tsgId]?.vertical,
      }
    }
    return each
  })
  return instancesAndTree
}
/**
 * Find the parent root node of the
 * @param sgKeyBy
 * @param node
 * @returns {*}
 */
export const findParentRootNode = (sgKeyBy, node) => {
  if (!node || node.parent_id === '_superRoot') {
    return node
  }
  return findParentRootNode(sgKeyBy, sgKeyBy[node.parent_id])
}
/**
 *  Prepare the Tsg Dropdown based on the selectedTreeNode for claiming the license
 * @param nodeList
 * @param collection
 * @returns {*}
 */
//Uncomment if the we need to pull all the parent tsgs of the current node
// const prepareTSGDropDown = (node, collection) => {
//   if (Array.isArray(node)) {
//     node?.forEach((elem) => {
//       if (elem.children && elem.children.length) {
//         prepareTSGDropDown(elem.children, collection)
//       }
//       collection.push({ value: elem.display_name, description: elem.display_name, key: elem.id })
//     })
//   }
//   else if (node) {
//     prepareTSGDropDown([node], collection)
//   }
//   return collection
// }

const prepareTSGDropDown = (nodeList, collection) => {
  nodeList?.forEach((elem) => {
    if (elem.parent_id === ROOT_NODE_ID) {
      collection.push({ value: elem.display_name, description: elem.display_name, key: elem.id, children: elem.display_name })
    }
  })
}

const handleLoadTreeData = (action = { sgTree: [] }, state = {}) => {
  const { sgTree = [] } = action || {}
  if (sgTree) {

    const tree = sgTree.map(each => ({ ...each, parent_id: ROOT_NODE_ID, isRoot: true }))
    const findRootNode = tree[0]

    if (!!findRootNode) {

      const flattenTree = []
      const sgTree = [{ display_name: 'All Tenants', id: ROOT_NODE_ID, isSuperRoot: true, children: tree }]
      FlattenTree(sgTree, flattenTree)

      const sgKeyBy = keyBy(flattenTree, 'id')

      let selectedTreeNode

      if (state.selectedTreeNode && sgKeyBy.hasOwnProperty(state.selectedTreeNode.id)) {
        selectedTreeNode = sgKeyBy[state.selectedTreeNode.id]
      }
      else if (state.globalTsgId) {
        selectedTreeNode = flattenTree.find(({ id }) => id === state.globalTsgId)
      }
      else {
        selectedTreeNode = findRootNode
      }

      selectedTreeNode = Object.assign({
        id: '',
        display_name: '',
        key: '',
        children: [],
        isAbsoluteRoot: false,
        isRoot: false,
        parent_id: '',
        title: '',
        vertical: '',
        tsg_id: ''
      }, selectedTreeNode)

      // if (getCommonModuleState()?.tsg !== selectedTreeNode) {
      //   setSelectedTSG(selectedTreeNode)
      // }
      if (getCommonModuleState()?.tsg !== selectedTreeNode) {
        setTimeout(() => { // prevent update state in reducer
          setSelectedTSG(selectedTreeNode)
        }, 10)
      }

      const root = findParentRootNode(sgKeyBy, selectedTreeNode)
      const tsgDropDown = []
      prepareTSGDropDown(flattenTree, tsgDropDown)

      const breadcrumbs = new Map()
      createBreadCrumbs(selectedTreeNode, breadcrumbs, sgKeyBy)

      breadcrumbs.set('_superRoot', { display_name: 'All Tenant', id: ROOT_NODE_ID, isSuperRoot: true })

      const globalTsgName = sgKeyBy[state.globalTsgId]?.display_name || ''

      return {
        ...state,
        sgTree,
        selectedTreeNode,
        breadcrumbs,
        flattenTree,
        selectedTreeRoot: root,
        tsgDropDownDetails: tsgDropDown,
        globalTsgName,
        sgKeyBy,
      }
    }
  }
  return { ...state }
}

const handleSelectTreeNode = (action, state) => {
  /**
   * Tree selection logic
   * This will update bredcrumb object as well.
   */
  const key = action.selectedNode?.node?.id || action.selectedNode?.id
  const selectedTreeNode = state.sgKeyBy[key] ?? action.selectedNode
  const breadcrumbs = new Map()
  if (selectedTreeNode) {
    createBreadCrumbs(selectedTreeNode, breadcrumbs, state.sgKeyBy)
  }
  breadcrumbs.set('_superRoot', { display_name: 'All Tenants', id: '_superRoot', isSuperRoot: true })

  if (getCommonModuleState()?.tsg !== selectedTreeNode) {
    setTimeout(() => { // prevent update state in reducer
      setSelectedTSG(selectedTreeNode)
    }, 10)
  }

  return { ...state, selectedTreeNode, breadcrumbs }
}



/**
 * ============================
 *    CAUTION:
 *    Modification in this required to make sure Tree working as expected
 * ============================
 *
 * Tree reducer is responsible to keep data for Left Tree which shared between Tenant Management, Subscription and IAM management
 * Same data also used in Tenant Switcher component.
 * @param state
 * @param action
 * @returns {{flattenTree: *[], sgTree: *[], sgKeyBy: {}, instances: *[], breadcrumbs: Map<any, any>}|{flattenTree: [], selectedTreeNode, sgTree: [], sgKeyBy: {}, instances: [], breadcrumbs: Map<any, any>}|{flattenTree: [], sgTree: [], sgKeyBy: {}, instances: ([]|Array<T>|T[]|[]|*), instancesKeyBy: Dictionary<unknown>, breadcrumbs: *}|{flattenTree: [], selectedTreeNode: unknown, sgTree: ([]|[]|*), sgKeyBy: Dictionary<unknown>, instances: [], breadcrumbs: Map<any, any>}}
 * @constructor
 */
const TreeReducer = (state = getInitialState(), action) => {
  switch (action.type) {
    /**
     * Load Tree Data and create flatten and keyby tree values for easy compuatation
     */
    case LOAD_TREE_DATA: {
      return handleLoadTreeData(action, state)
    }
    case SELECTED_TREE_NODE: {
      return handleSelectTreeNode(action, state)
    }
    case LOAD_INSTANCE_DATA: {
      const instancesAndTreeNode = getInstancesBasedOnTreeNode(state, action.instances)
      return {
        ...state,
        instances: action.instances,
        instancesAndTreeNode,
        instancesKeyBy: groupBy(action.instances, 'tsg_id')
      }
    }
    case ON_EXPANDED_KEY: {
      return { ...state, expandedKeys: action.expandedKeys }
    }
    case LOAD_TENANT_INSTANCE_DATA: {
      return {
        ...state,
        parentTenantInstances: action.instances,
        childTenantInstances: action.instances
      }
    }
    case LOAD_CHILD_INSTANCE_DATA: {
      const childInstance = action.instances ? action.instances.filter((each) => {
        return each.tsg_id === state.selectedTreeNode?.id
      }) : []
      return { ...state, childTenantInstances: childInstance }
    }
    case LOAD_TSG_ID: {
      return { ...state, globalTsgId: action.tsgId }
    }
    case RESET_TREE_DATA: {
      return initialState
    }
    default: {
      return state
    }
  }
}
TreeReducer.initialState = initialState
export default TreeReducer
